import React, { useCallback } from 'react'
import './prompt-editor.scss'
import { observer } from 'mobx-react'
import { Col, notification, Row } from 'antd'
import { AppService } from '../../../services'
import { PromptService } from '../../../services/prompt'
import { Sticky } from '../../../components/sticky/sticky'
import { AppState } from '../../../stores/app'
import { DeliveryChannelSelector } from '@pushly/aqe/lib/components/delivery-channel-selector/delivery-channel-selector'
import { PromptStyle } from '@pushly/models/lib/enums/prompts/prompt-style'
import { FormLayoutRow, PageHeader, Well } from '@pushly/aqe/lib/components'
import {
    isGetSlidePromptResponse,
    UpdateSlidePromptRequest,
} from '@pushly/models/lib/structs/prompts/styles/slide/slide-prompt'
import {
    CreateNativePromptRequest,
    isGetNativePromptResponse,
    UpdateNativePromptRequest,
} from '@pushly/models/lib/structs/prompts/styles/native/native-prompt'
import { PromptEditorTreatment } from './types'
import { NativePromptConfig } from '@pushly/models/lib/structs/prompts/styles/native/native-prompt-config'
import {
    isGetCustomPromptResponse,
    isUpdatePromptGroupRequest,
    UpdateCustomPromptRequest,
} from '@pushly/models/lib/structs'
import { TwoStepFormWarning } from './two-step-form-warning'
import { CustomPromptPreviewStub } from './previews/custom-prompt-preview-stub'
import { PromptPreview } from './previews/prompt-preview'
import { PromptGroupDetailsEditor } from './prompt-group/prompt-group-details-editor'
import { CreatePromptGroupRequest } from '@pushly/models/lib/structs/prompts/create-prompt-group-request'
import { PromptGroupOptionsEditor } from './prompt-group/prompt-group-options-editor'
import { PromptTreatmentsManager } from './prompt/prompt-treatments-manager'
import { PromptGroupPageConditionsEditor } from './prompt-group/prompt-group-page-conditions-editor'
import {
    DEFAULT_COOKIE_LENGTH_SECONDS,
    DEFAULT_DESKTOP_DISPLAY_CONDITION,
    DEFAULT_MOBILE_DISPLAY_CONDITION,
    DEFAULT_PROMPT_GROUP_CHANNEL,
    PromptErrorFieldNamesMap,
} from './constants'
import { PromptEditorDispatchContext, promptEditorReducer } from './reducer'
import { PromptEditorContext, PromptEditorContextState } from './context'
import { useService } from '@pushly/aqe/lib/hooks/use-service'
import { useActiveDomainChangeEffect } from '../../../hooks/use-active-domain-change-effect'
import { useParams } from 'react-router-dom'
import { DeliveryChannel } from '@pushly/models/lib/enums/delivery-channels.enum'
import { simpleFormErrorNotification, simpleNotification } from '@pushly/aqe/lib/utils'
import { InvalidFieldException } from '@pushly/models/lib/exceptions/invalid-field-exception'
import { MissingFieldException } from '@pushly/models/lib/exceptions/missing-field-exception'
import { extractFriendlyErrorMessage } from '../../../exceptions/helpers'
import { PromptBehavior } from '@pushly/models/lib/structs/prompts/prompt-behavior'
import { validatePromptGroup } from './helpers'
import { GetPromptGroupResponse } from '@pushly/models/lib/structs/prompts/get-prompt-group-response'
import { IApiResponse } from '@pushly/aqe/lib/interfaces'
import { UpdateAbstractPromptRequest } from '@pushly/models/lib/structs/prompts/styles/update-abstract-prompt-request'
import { PromptGroupConditions } from '@pushly/models/lib/structs/prompts/prompt-group-conditions'
import { WebPromptGroupDisplayConditions } from '@pushly/models/lib/structs/prompts/prompt-group-display-conditions'

function getCreatePromptGroupStub(): CreatePromptGroupRequest {
    return CreatePromptGroupRequest.from({
        channel: DEFAULT_PROMPT_GROUP_CHANNEL,
        name: undefined!,
        isActive: true,
        isAbTest: false,
        displayToPct: 100,
        priority: undefined!,
        prompts: undefined!,
        conditions: PromptGroupConditions.from({
            display: WebPromptGroupDisplayConditions.from({
                desktop: DEFAULT_DESKTOP_DISPLAY_CONDITION,
                mobile: DEFAULT_MOBILE_DISPLAY_CONDITION,
            }),
        }),
    })
}

function getDefaultTreatmentStub(): PromptEditorTreatment {
    return CreateNativePromptRequest.from({
        style: PromptStyle.NATIVE,
        weight: 100,
        isAutoShow: true,
        config: NativePromptConfig.from({
            behavior: PromptBehavior.from({
                cookieLengthSeconds: DEFAULT_COOKIE_LENGTH_SECONDS,
            }),
        }),
    })
}

export const PromptEditor = observer(() => {
    const appState = useService(AppState)
    const appSvc = useService(AppService)
    const promptSvc = useService(PromptService)

    const [context, dispatch] = React.useReducer(promptEditorReducer, {
        loading: true,
        activeDeliveryChannels: [DeliveryChannel.WEB],
        selectedDeliveryChannel: DeliveryChannel.WEB,
        isAutoShowEnabled: true,
        isAbTestEnabled: false,
    })

    const params = useParams<{ promptId?: string; template?: string }>()
    const reqGroupIdStr = params.promptId || params.template

    useActiveDomainChangeEffect((_domain, svc) => {
        svc.routeWithinDomain('/prompts')
    })

    // domain set effect
    React.useEffect(() => {
        if (appState.currentDomain?.id) {
            dispatch({ type: 'set-domain', domain: appState.currentDomain })
        }
    }, [appState.currentDomain?.id])

    React.useEffect(() => {
        let loading = false

        async function loadRequestedPromptGroup() {
            if (!context.domain?.id || loading) {
                return
            }
            loading = true

            function dispatchSetDefaults() {
                dispatch({ type: 'set-prompt-group', group: getCreatePromptGroupStub() })
                dispatch({ type: 'set-prompts', prompts: [getDefaultTreatmentStub()] })
            }

            if (!reqGroupIdStr) {
                dispatchSetDefaults()
                return
            }

            let groupId: number
            try {
                groupId = parseInt(reqGroupIdStr, 10)
            } catch (err) {
                console.warn(`Failed to parse requested PromptGroup ID: ${reqGroupIdStr}`, err)

                dispatchSetDefaults()
                return
            }

            const res = await promptSvc.fetchPromptGroupById(context.domain.id || 0, groupId)
            if (!res.ok || !res.data) {
                if (!res.cancelled) {
                    if (res.error) {
                        console.error(res.error)
                    } else {
                        console.warn('Failed to fetch requested PromptGroup')
                    }
                }

                dispatchSetDefaults()
                return
            }

            const promptGroup = res.data
            dispatch({ type: 'set-prompt-group-source', group: promptGroup })

            if (!promptGroup.prompts.length) {
                dispatch({ type: 'set-prompts', prompts: [getDefaultTreatmentStub()] })
                return
            }

            const prompts = promptGroup.prompts.map((p) => {
                if (isGetSlidePromptResponse(p)) {
                    return UpdateSlidePromptRequest.from(p)
                }
                if (isGetNativePromptResponse(p)) {
                    return UpdateNativePromptRequest.from(p)
                }
                if (isGetCustomPromptResponse(p)) {
                    return UpdateCustomPromptRequest.from(p)
                }
                console.warn('Unknown prompt style detected', p)
                return UpdateAbstractPromptRequest.from(p)
            })

            dispatch({ type: 'set-prompts', prompts })
            dispatch({ type: 'set-is-auto-show-enabled', enabled: prompts[0]?.isAutoShow ?? true })
            dispatch({
                type: 'set-is-ab-test-enabled',
                enabled: prompts.length > 1,
                // skip reset on the initial load to keep all prompts and loaded weights
                skipPromptsReset: true,
            })
        }

        loadRequestedPromptGroup()
    }, [context.domain?.id, reqGroupIdStr])

    const handleSubmit = useCallback(
        async (ctx: PromptEditorContextState) => {
            if (ctx.loading) return

            notification.destroy()

            try {
                const promptGroup = validatePromptGroup(ctx)

                let response: IApiResponse<GetPromptGroupResponse>
                if (isUpdatePromptGroupRequest(promptGroup)) {
                    response = await promptSvc.updatePromptGroupById(
                        ctx.domain.id,
                        parseInt(reqGroupIdStr!, 10),
                        promptGroup,
                    )
                } else {
                    response = await promptSvc.createPromptGroup(ctx.domain.id, promptGroup)
                }

                if (response.ok && response.data) {
                    simpleNotification(
                        'success',
                        <span>
                            Prompt "{response.data.name}" was successfully saved. Changes may take up to 10 minutes to
                            take effect.
                        </span>,
                    )

                    appSvc.routeWithinDomain('/prompts')
                }
            } catch (error) {
                if (error instanceof InvalidFieldException || error instanceof MissingFieldException) {
                    error = extractFriendlyErrorMessage(error, {
                        rootModel: error.context.constructor,
                        fieldNamesMap: PromptErrorFieldNamesMap,
                        startWithProperty: true,
                    })
                }

                if (error.response?.data?.message) {
                    simpleNotification('error', error.response.data.message)
                } else {
                    if (error instanceof Error) {
                        simpleNotification('error', error.message)
                    } else {
                        simpleFormErrorNotification(error)
                    }
                }
            }
        },
        [reqGroupIdStr],
    )

    const handleCancel = () => appSvc.routeBack()

    const canShowChannels = context.activeDeliveryChannels.length > 1
    const canShowBuilder = context.selectedDeliveryChannel === DeliveryChannel.WEB

    return (
        <div className="prompt-editor-page">
            <PromptEditorContext.Provider value={context}>
                <PromptEditorDispatchContext.Provider value={dispatch}>
                    <PageHeader
                        className={`mode-${params.promptId ? 'edit' : 'create'}`}
                        loading={context.loading}
                        title={params.promptId ? 'Edit Prompt' : 'Create Prompt'}
                        append={params.promptId && `#${params.promptId}`}
                    />

                    <div className="prompt-editor-form">
                        <Row className="page-content" gutter={18}>
                            <Col className="page-content-left" span={14}>
                                {canShowChannels && (
                                    <Well title="Channel" hideFooter={true}>
                                        <FormLayoutRow
                                            className="prompt-group-channel-select"
                                            loading={context.loading}
                                            skeleton={{ width: 240 }}
                                            layout="vertical"
                                            label={null}
                                            value={
                                                !context.loading && (
                                                    <DeliveryChannelSelector
                                                        type="single"
                                                        nested={true}
                                                        visibleChannels={context.activeDeliveryChannels}
                                                        value={context.selectedDeliveryChannel}
                                                        onChange={(channel) => {
                                                            dispatch({ type: 'set-selected-delivery-channel', channel })
                                                        }}
                                                    />
                                                )
                                            }
                                        />
                                    </Well>
                                )}

                                {!canShowBuilder && <TwoStepFormWarning />}

                                {canShowBuilder && (
                                    <>
                                        <PromptGroupDetailsEditor />
                                        <PromptGroupPageConditionsEditor />
                                        <PromptTreatmentsManager />
                                        <PromptGroupOptionsEditor
                                            onSubmit={() => handleSubmit(context)}
                                            onCancel={handleCancel}
                                        />
                                    </>
                                )}
                            </Col>

                            {canShowBuilder && (
                                <Col className="page-content-right" span={10}>
                                    <Sticky threshold={28}>
                                        {context.selectedPrompt?.style === PromptStyle.PUBLISHER_CUSTOM ? (
                                            <CustomPromptPreviewStub />
                                        ) : (
                                            <PromptPreview />
                                        )}
                                    </Sticky>
                                </Col>
                            )}
                        </Row>
                    </div>
                </PromptEditorDispatchContext.Provider>
            </PromptEditorContext.Provider>
        </div>
    )
})

/* webpack-strip-block:removed */
